// Google Ads / dataLayer tracking for the public site.
// Change the conversion labels here once they are created in Google Ads
// (Outils > Conversions > détails de la conversion > "send_to": AW-XXXX/LABEL).

export const GOOGLE_ADS_ID = "AW-18051591424";
export const LEAD_FORM_CONVERSION_LABEL = "LEAD_FORM";
export const CALL_CLICK_CONVERSION_LABEL = "CLICK_CALL";
export const WHATSAPP_CLICK_CONVERSION_LABEL = "CLICK_WHATSAPP";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const sendTo = (label: string) => `${GOOGLE_ADS_ID}/${label}`;

function pushDataLayer(payload: Record<string, unknown>) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

function gtagEvent(name: string, params: Record<string, unknown>) {
  if (typeof window.gtag === "function") window.gtag("event", name, params);
}

/** Call only after the lead was stored successfully. */
export function trackLeadSubmit() {
  gtagEvent("conversion", { send_to: sendTo(LEAD_FORM_CONVERSION_LABEL) });
  pushDataLayer({ event: "lead_form_submit" });
}

export type ContactClickKind = "call" | "whatsapp";

/** tel: -> call, wa.me / api.whatsapp.com -> whatsapp, anything else (mailto, ...) -> null. */
export function classifyContactHref(href: string | null | undefined): ContactClickKind | null {
  if (!href) return null;
  const h = href.trim().toLowerCase();
  if (h.startsWith("tel:")) return "call";
  if (/^https?:\/\/(wa\.me|api\.whatsapp\.com)\//.test(h)) return "whatsapp";
  return null;
}

export function trackContactClick(kind: ContactClickKind) {
  const label = kind === "call" ? CALL_CLICK_CONVERSION_LABEL : WHATSAPP_CLICK_CONVERSION_LABEL;
  const event = kind === "call" ? "click_call" : "click_whatsapp";
  pushDataLayer({ event });
  gtagEvent("conversion", { send_to: sendTo(label) });
}

/** Internal staff pages: calling a client from there is not a customer conversion. */
const isInternalPath = (path: string) => path.startsWith("/gestion") || path.startsWith("/admin");

/** One delegated listener covers every tel: / WhatsApp link (header, hero, floating, mobile bar...). */
export function installContactClickTracking(doc: Document = document) {
  const handler = (e: Event) => {
    if (isInternalPath(window.location.pathname)) return;
    const target = e.target as Element | null;
    const link = target?.closest?.("a[href]") as HTMLAnchorElement | null;
    const kind = classifyContactHref(link?.getAttribute("href"));
    if (kind) trackContactClick(kind);
  };
  doc.addEventListener("click", handler, true);
  return () => doc.removeEventListener("click", handler, true);
}

// ---------------------------------------------------------------------------
// Attribution (gclid / utm) captured on arrival, kept for the browser session.
// ---------------------------------------------------------------------------

export const ATTRIBUTION_KEY = "pi_attribution";

export interface Attribution {
  gclid?: string;
  utm_source?: string;
  utm_campaign?: string;
  landing_page?: string;
}

const clip = (v: string | null, max: number) => (v ? v.slice(0, max) : undefined);

export function captureAttribution(search: string = window.location.search, path: string = window.location.pathname) {
  try {
    const params = new URLSearchParams(search);
    const next: Attribution = {
      gclid: clip(params.get("gclid"), 300),
      utm_source: clip(params.get("utm_source"), 200),
      utm_campaign: clip(params.get("utm_campaign"), 200),
    };
    const hasNew = next.gclid || next.utm_source || next.utm_campaign;
    const existing = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (!hasNew && existing) return;
    sessionStorage.setItem(
      ATTRIBUTION_KEY,
      JSON.stringify({ ...next, landing_page: path.slice(0, 500) }),
    );
  } catch {
    /* sessionStorage unavailable (private mode) */
  }
}

export function getAttribution(): Attribution {
  try {
    return JSON.parse(sessionStorage.getItem(ATTRIBUTION_KEY) || "{}") as Attribution;
  } catch {
    return {};
  }
}
